import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import type { AxisTriplet } from '../../../lib/skeleton/boneTransforms';
import type { Bone } from '../../../lib/skeleton/core';
import ResetToDefaultButton from '../../../presentations/ResetToDefaultButton';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import {
  INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL,
  PREFERRED_ANGLES_TOOLTIP_TEXT,
} from '../constants';
import { ensurePreferredAngles, pruneBonePropertiesDefaults } from '../utils';
import {
  ZERO_AXIS_TRIPLET,
  axisTripletDiffersFromBaseline,
} from './axisTripletFieldUtils';
import TransformAxisValuesRow from './TransformAxisValuesRow';

interface PreferredAnglesEditorProps {
  bone: Bone;
  bump: () => void;
  compactLayout: boolean;
  onHistoryAction?: (label: string, action: () => void) => void;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}

function readPreferredAnglesValues(bone: Bone): AxisTriplet {
  const preferredAngles = bone.properties?.preferredAngles;
  if (!preferredAngles) return ZERO_AXIS_TRIPLET;
  return [preferredAngles.x, preferredAngles.y, preferredAngles.z];
}

export default function PreferredAnglesEditor({
  bone,
  bump,
  compactLayout,
  onHistoryAction,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: PreferredAnglesEditorProps) {
  const values = readPreferredAnglesValues(bone);
  const isDefaultPreferredAngles = !axisTripletDiffersFromBaseline(
    values,
    ZERO_AXIS_TRIPLET,
    0,
  );

  const setAxis = (axisIndex: number, nextValue: number) => {
    const nextValues = [...values] as AxisTriplet;
    nextValues[axisIndex] = nextValue;

    if (axisTripletDiffersFromBaseline(nextValues, ZERO_AXIS_TRIPLET, 0)) {
      const preferredAngles = ensurePreferredAngles(bone);
      preferredAngles.x = nextValues[0];
      preferredAngles.y = nextValues[1];
      preferredAngles.z = nextValues[2];
    } else if (bone.properties) {
      bone.properties.preferredAngles = null;
      pruneBonePropertiesDefaults(bone);
    }

    bump();
  };

  const resetPreferredAngles = () => {
    if (!bone.properties) return;
    bone.properties.preferredAngles = null;
    pruneBonePropertiesDefaults(bone);
    bump();
  };

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2 py-0.5">
        <Tooltip text={PREFERRED_ANGLES_TOOLTIP_TEXT} side="top" inModal>
          <span className="inline-flex">
            <Text variant="studio/body/medium/xs" className={PANEL_WRITING_CLASSNAME}>
              Preferred Angle Bias
            </Text>
          </span>
        </Tooltip>
        <ResetToDefaultButton
          label="preferred angles"
          isDefault={isDefaultPreferredAngles}
          onReset={() =>
            onHistoryAction
              ? onHistoryAction('Reset preferred angle bias', resetPreferredAngles)
              : resetPreferredAngles()
          }
        />
      </div>
      <TransformAxisValuesRow
        label="Rotate"
        labelIcon={<RotateIcon className="h-3 w-3" />}
        compact={compactLayout}
        values={values}
        editable
        ariaLabelPrefix="Preferred Angle Bias"
        onAxisChange={setAxis}
        baselineValues={ZERO_AXIS_TRIPLET}
        changeEpsilon={0}
        dragDeltaPerPixel={INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL}
        onValueChangeStart={onValueChangeStart}
        onValueChangeEnd={onValueChangeEnd}
        onDragValueChangeStart={onDragValueChangeStart}
      />
    </div>
  );
}
